import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "device-reading-dataset app-page app-page--fill" }
const _hoisted_2 = { class: "dataset-panel" }
const _hoisted_3 = { class: "toolbar" }
const _hoisted_4 = { class: "toolbar-actions" }
const _hoisted_5 = { class: "photo-cell" }
const _hoisted_6 = {
  key: 0,
  class: "photo-loading"
}
const _hoisted_7 = {
  key: 2,
  class: "photo-empty"
}
const _hoisted_8 = ["title"]
const _hoisted_9 = { class: "reading-list" }
const _hoisted_10 = { class: "reading-title" }
const _hoisted_11 = { class: "reading-value" }
const _hoisted_12 = {
  key: 0,
  class: "empty-text"
}
const _hoisted_13 = { class: "reading-list" }
const _hoisted_14 = { class: "reading-title" }
const _hoisted_15 = { class: "reading-value" }
const _hoisted_16 = {
  key: 0,
  class: "empty-text"
}
const _hoisted_17 = { class: "compare-list" }
const _hoisted_18 = { class: "compare-title" }
const _hoisted_19 = { class: "compare-values" }
const _hoisted_20 = {
  key: 0,
  class: "empty-text"
}
const _hoisted_21 = { class: "pagination-bar" }

import { reactive, ref, onMounted, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import { Loading, Refresh } from '@element-plus/icons-vue'
import DeviceReadingDatasetApi, {
  type DeviceReadingDatasetItem,
  type ReadingValue,
} from '@/network/apis/DeviceReadingDataset'
import { list } from '@/network'
import { useRouteTitle } from '@/utils/useRouteTitle'
import { useButtonPermission } from '@/utils/buttonPermission'

interface PhotoState {
  url: string
  loading: boolean
  error: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

useRouteTitle('设备读数数据集')
const { can } = useButtonPermission('/visualModel/DeviceReadingDataset/index.vue')

const loading = ref(false)
const tableData = ref<DeviceReadingDatasetItem[]>([])
const statusFilter = ref('')
const photoMap = ref<Record<string, PhotoState>>({})

const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0,
})

const getResponseData = (res: any) => res?.response || res

const buildStatusFilters = () => {
  if (!statusFilter.value) return []
  return [
    {
      FieldName: 'AI读数状态',
      FiedType: 'string',
      Type: 3,
      Value1: statusFilter.value,
      Value2: '',
      Ifalso: true,
    },
  ]
}

const formatTime = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const pad = (num: number) => String(num).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

const safeParseJson = (value?: string) => {
  if (!value || typeof value !== 'string') return null
  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

const normalizeReadings = (readings?: ReadingValue[] | string, jsonValue?: string): ReadingValue[] => {
  const source = Array.isArray(readings)
    ? readings
    : safeParseJson(typeof readings === 'string' ? readings : jsonValue)

  if (!Array.isArray(source)) return []
  return source.map((item: any) => ({
    title: String(item?.title ?? item?.Title ?? item?.名称 ?? ''),
    value: item?.value ?? item?.Value ?? item?.值 ?? '',
  }))
}

const formatReadingValue = (value: unknown) => {
  if (value === undefined || value === null || value === '') return '-'
  return String(value)
}

const isSameReadingValue = (aiValue: unknown, actualValue: unknown) => {
  return formatReadingValue(aiValue).trim() === formatReadingValue(actualValue).trim()
}

const buildReadingCompare = (row: DeviceReadingDatasetItem) => {
  const aiReadings = normalizeReadings(row.AI读数, row.AI读数json)
  const actualReadings = normalizeReadings(row.实际读数, row.实际读数json)
  const titleSet = new Set<string>()
  aiReadings.forEach(item => titleSet.add(item.title || '-'))
  actualReadings.forEach(item => titleSet.add(item.title || '-'))

  return Array.from(titleSet).map((title) => {
    const ai = aiReadings.find(item => (item.title || '-') === title)
    const actual = actualReadings.find(item => (item.title || '-') === title)
    return {
      title,
      ai: formatReadingValue(ai?.value),
      actual: formatReadingValue(actual?.value),
      mismatched: !isSameReadingValue(ai?.value, actual?.value),
    }
  })
}

const getStatusTagType = (status?: string) => {
  if (status === '正常') return 'success'
  if (status === '异常') return 'danger'
  return 'info'
}

const getPhotoState = (row: DeviceReadingDatasetItem): PhotoState => {
  const fileName = row.文件名 || ''
  return photoMap.value[fileName] || { url: '', loading: false, error: false }
}

const clearPhotos = () => {
  Object.values(photoMap.value).forEach((item) => {
    if (item.url?.startsWith('blob:')) {
      URL.revokeObjectURL(item.url)
    }
  })
  photoMap.value = {}
}

const updatePhotoState = (fileName: string, patch: Partial<PhotoState>) => {
  const current = photoMap.value[fileName] || { url: '', loading: false, error: false }
  if (current.url?.startsWith('blob:') && patch.url && patch.url !== current.url) {
    URL.revokeObjectURL(current.url)
  }
  photoMap.value = {
    ...photoMap.value,
    [fileName]: {
      ...current,
      ...patch,
    },
  }
}

const loadPhotos = (rows: DeviceReadingDatasetItem[]) => {
  const fileNames = Array.from(new Set(rows.map(row => row.文件名).filter(Boolean))) as string[]
  fileNames.forEach((fileName) => {
    const cachedPhoto = photoMap.value[fileName]
    if (cachedPhoto?.url || cachedPhoto?.loading) {
      return
    }

    updatePhotoState(fileName, { url: '', loading: true, error: false })
    list.downloadPhoto({ 文件名: fileName })
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        updatePhotoState(fileName, { url, loading: false, error: false })
      })
      .catch((error) => {
        console.error(`加载现场照片失败: ${fileName}`, error)
        updatePhotoState(fileName, { url: '', loading: false, error: true })
      })
  })
}

const loadData = async () => {
  loading.value = true
  try {
    const res: any = await DeviceReadingDatasetApi.list(
      {
        page: pagination.page,
        key: '',
        intPageSize: pagination.pageSize,
      },
      buildStatusFilters()
    )
    const response = getResponseData(res)
    tableData.value = Array.isArray(response?.data) ? response.data : []
    pagination.total = Number(response?.dataCount || tableData.value.length || 0)
    loadPhotos(tableData.value)
  } catch (error) {
    console.error('加载设备读数数据集列表失败:', error)
    tableData.value = []
    pagination.total = 0
    ElMessage.error('加载设备读数数据集列表失败')
  } finally {
    loading.value = false
  }
}

const handleStatusChange = () => {
  pagination.page = 1
  loadData()
}

const handlePageChange = (page: number) => {
  pagination.page = page
  loadData()
}

const handlePageSizeChange = (pageSize: number) => {
  pagination.pageSize = pageSize
  pagination.page = 1
  loadData()
}

onMounted(() => {
  loadData()
})

onBeforeUnmount(() => {
  clearPhotos()
})

return (_ctx: any,_cache: any) => {
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_image = _resolveComponent("el-image")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_el_select, {
          modelValue: statusFilter.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((statusFilter).value = $event)),
          placeholder: "AI读数状态",
          clearable: "",
          class: "status-filter",
          onChange: handleStatusChange,
          onClear: handleStatusChange
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_option, {
              label: "正常",
              value: "正常"
            }),
            _createVNode(_component_el_option, {
              label: "异常",
              value: "异常"
            })
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createElementVNode("div", _hoisted_4, [
          (_unref(can)('查询'))
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 0,
                type: "primary",
                circle: "",
                size: "small",
                class: "app-refresh-btn",
                icon: _unref(Refresh),
                loading: loading.value,
                onClick: loadData,
                title: "刷新"
              }, null, 8, ["icon", "loading"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _withDirectives((_openBlock(), _createBlock(_component_el_table, {
        data: tableData.value,
        border: "",
        stripe: "",
        class: "dataset-table",
        height: "100%"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_table_column, {
            type: "index",
            label: "序号",
            width: "70",
            align: "center"
          }),
          _createVNode(_component_el_table_column, {
            label: "现场照片",
            width: "150",
            align: "center"
          }, {
            default: _withCtx(({ row }) => [
              _createElementVNode("div", _hoisted_5, [
                (getPhotoState(row).loading)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      _createVNode(_component_el_icon, { class: "is-loading" }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Loading))
                        ]),
                        _: 1
                      })
                    ]))
                  : (getPhotoState(row).url)
                    ? (_openBlock(), _createBlock(_component_el_image, {
                        key: 1,
                        class: "photo-thumb",
                        src: getPhotoState(row).url,
                        "preview-src-list": _unref(can)('照片查看') ? [getPhotoState(row).url] : [],
                        fit: "cover",
                        "preview-teleported": ""
                      }, null, 8, ["src", "preview-src-list"]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_7, "无图")),
                _createElementVNode("div", {
                  class: "file-name",
                  title: row.文件名 || ''
                }, _toDisplayString(row.文件名 || '-'), 9, _hoisted_8)
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_el_table_column, {
            prop: "上传时间",
            label: "上传时间",
            "min-width": "170",
            "show-overflow-tooltip": ""
          }, {
            default: _withCtx(({ row }) => [
              _createTextVNode(_toDisplayString(formatTime(row.上传时间)), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_el_table_column, {
            prop: "AI读数状态",
            label: "AI读数状态",
            width: "110",
            align: "center"
          }, {
            default: _withCtx(({ row }) => [
              _createVNode(_component_el_tag, {
                type: getStatusTagType(row.AI读数状态)
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(row.AI读数状态 || '-'), 1)
                ]),
                _: 2
              }, 1032, ["type"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_table_column, {
            label: "AI读数",
            "min-width": "220"
          }, {
            default: _withCtx(({ row }) => [
              _createElementVNode("div", _hoisted_9, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(normalizeReadings(row.AI读数, row.AI读数json), (item) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: `${item.title}-${item.value}`,
                    class: "reading-item"
                  }, [
                    _createElementVNode("span", _hoisted_10, _toDisplayString(item.title || '-'), 1),
                    _createElementVNode("span", _hoisted_11, _toDisplayString(formatReadingValue(item.value)), 1)
                  ]))
                }), 128)),
                (normalizeReadings(row.AI读数, row.AI读数json).length === 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_12, "-"))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_el_table_column, {
            label: "实际读数",
            "min-width": "220"
          }, {
            default: _withCtx(({ row }) => [
              _createElementVNode("div", _hoisted_13, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(normalizeReadings(row.实际读数, row.实际读数json), (item) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: `${item.title}-${item.value}`,
                    class: "reading-item"
                  }, [
                    _createElementVNode("span", _hoisted_14, _toDisplayString(item.title || '-'), 1),
                    _createElementVNode("span", _hoisted_15, _toDisplayString(formatReadingValue(item.value)), 1)
                  ]))
                }), 128)),
                (normalizeReadings(row.实际读数, row.实际读数json).length === 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_16, "-"))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_el_table_column, {
            label: "读数对比",
            "min-width": "260"
          }, {
            default: _withCtx(({ row }) => [
              _createElementVNode("div", _hoisted_17, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(buildReadingCompare(row), (item) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: item.title,
                    class: _normalizeClass(["compare-item", { 'compare-item--mismatch': item.mismatched }])
                  }, [
                    _createElementVNode("span", _hoisted_18, _toDisplayString(item.title), 1),
                    _createElementVNode("span", _hoisted_19, [
                      _createElementVNode("span", null, "AI: " + _toDisplayString(item.ai), 1),
                      _createElementVNode("span", null, "实际: " + _toDisplayString(item.actual), 1)
                    ])
                  ], 2))
                }), 128)),
                (buildReadingCompare(row).length === 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_20, "-"))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          }),
          _createVNode(_component_el_table_column, {
            prop: "上传人ID",
            label: "上传人ID",
            "min-width": "150",
            "show-overflow-tooltip": ""
          }, {
            default: _withCtx(({ row }) => [
              _createTextVNode(_toDisplayString(row.上传人ID || '-'), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["data"])), [
        [_directive_loading, loading.value]
      ]),
      _createElementVNode("div", _hoisted_21, [
        _createVNode(_component_el_pagination, {
          "current-page": pagination.page,
          "onUpdate:currentPage": _cache[1] || (_cache[1] = ($event: any) => ((pagination.page) = $event)),
          "page-size": pagination.pageSize,
          "onUpdate:pageSize": _cache[2] || (_cache[2] = ($event: any) => ((pagination.pageSize) = $event)),
          "page-sizes": [10, 20, 50, 100],
          total: pagination.total,
          layout: "total, sizes, prev, pager, next, jumper",
          small: "",
          onSizeChange: handlePageSizeChange,
          onCurrentChange: handlePageChange
        }, null, 8, ["current-page", "page-size", "total"])
      ])
    ])
  ]))
}
}

})